/**
 * UTM Cookie Lifetime Extension for Chekly CheckoutManager
 * 
 * Extends the UTM tracking cookie lifetime from 7 days to 30 days (1 month).
 * This ensures UTM parameters persist longer for better attribution tracking.
 */

(function () {
  'use strict';

  const UTM_COOKIE_NAME = 'chekly_tracking';
  const COOKIE_LIFETIME_DAYS = 30;

  /**
   * Override setCookieWithExpire helper to extend UTM cookie lifetime
   * @param {Object} manager - CheckoutManager instance
   */
  function overrideCookieLifetime(manager) {
    if (!manager || !manager.helpers) {
      return false;
    }

    if (!manager.helpers.setCookieWithExpire) {
      return false;
    }

    if (manager.helpers.setCookieWithExpire.__utmOverrideApplied) {
      return true;
    }

    const originalSetCookieWithExpire = manager.helpers.setCookieWithExpire.bind(manager.helpers);

    manager.helpers.setCookieWithExpire = function(name, value, days) {
      if (name === UTM_COOKIE_NAME) {
        return originalSetCookieWithExpire(name, value, COOKIE_LIFETIME_DAYS);
      }
      return originalSetCookieWithExpire(name, value, days);
    };

    manager.helpers.setCookieWithExpire.__utmOverrideApplied = true;
    return true;
  }

  /**
   * Override init function to ensure cookie uses extended lifetime
   * @param {Object} manager - CheckoutManager instance
   */
  function overrideInitFunction(manager) {
    if (!manager || !manager.functions || !manager.functions.init) {
      return false;
    }

    if (manager.functions.init.__utmOverrideApplied) {
      return true;
    }

    const originalInit = manager.functions.init.bind(manager.functions);

    manager.functions.init = function() {
      overrideCookieLifetime(manager);
      return originalInit();
    };

    manager.functions.init.__utmOverrideApplied = true;
    return true;
  }

  /**
   * Get cookie value directly from document.cookie
   * @param {string} name - Cookie name
   * @returns {string|undefined} Cookie value or undefined
   */
  function getCookieDirect(name) {
    const escapedName = name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1');
    const regex = new RegExp(`(?:^|; )${escapedName}=([^;]*)`);
    const match = document.cookie.match(regex);
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  /**
   * Set cookie directly with extended lifetime
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Expiration in days
   */
  function setCookieDirect(name, value, days) {
    const expiration = new Date(Date.now() + 24 * 60 * 60 * 1000 * days).toUTCString();
    document.cookie = `${name}=${value}; expires=${expiration}; path=/;`;
  }

  /**
   * Update existing cookie to extend its lifetime
   * @param {Object} manager - CheckoutManager instance (optional)
   */
  function updateExistingCookie(manager) {
    const existingCookie = getCookieDirect(UTM_COOKIE_NAME);
    if (existingCookie) {
      try {
        const parsed = JSON.parse(existingCookie);
        if (parsed && Object.keys(parsed).length > 0) {
          setCookieDirect(UTM_COOKIE_NAME, existingCookie, COOKIE_LIFETIME_DAYS);
        }
      } catch (error) {
      }
    }
  }

  /**
   * Setup UTM cookie lifetime extension
   */
  function setupUtmCookieLifetimeExtension() {
    if (typeof window === 'undefined' || !window.checkoutManager) {
      setTimeout(setupUtmCookieLifetimeExtension, 10);
      return;
    }

    const manager = window.checkoutManager;
    
    overrideCookieLifetime(manager);
    overrideInitFunction(manager);
    updateExistingCookie(manager);
  }

  /**
   * Set up property interceptor to catch checkoutManager as soon as it's created
   */
  function setupPropertyInterceptor() {
    if (typeof window === 'undefined') {
      return;
    }

    let checkoutManagerValue = window.checkoutManager;
    
    Object.defineProperty(window, 'checkoutManager', {
      get: function() {
        return checkoutManagerValue;
      },
      set: function(value) {
        checkoutManagerValue = value;
        if (value && value.helpers) {
          setTimeout(() => {
            overrideCookieLifetime(value);
            overrideInitFunction(value);
          }, 0);
        }
      },
      configurable: true
    });

    if (checkoutManagerValue) {
      setTimeout(() => {
        overrideCookieLifetime(checkoutManagerValue);
        overrideInitFunction(checkoutManagerValue);
      }, 0);
    }
  }

  function trySetup() {
    if (typeof window !== 'undefined' && window.checkoutManager) {
      setupUtmCookieLifetimeExtension();
      return true;
    }
    return false;
  }

  updateExistingCookie();

  setupPropertyInterceptor();

  if (!trySetup()) {
    const checkInterval = setInterval(() => {
      updateExistingCookie();
      if (trySetup()) {
        clearInterval(checkInterval);
      }
    }, 10);
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        trySetup();
        clearInterval(checkInterval);
      });
    }
    
    window.addEventListener('load', () => {
      trySetup();
      clearInterval(checkInterval);
    });
    
    setTimeout(() => {
      clearInterval(checkInterval);
      trySetup();
    }, 2000);
  }
})();
